import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const ChatbotAuthContext = createContext();

export const useChatbotAuth = () => {
  const context = useContext(ChatbotAuthContext);
  if (!context) {
    throw new Error('useChatbotAuth must be used within a ChatbotAuthProvider');
  }
  return context;
};

export const ChatbotAuthProvider = ({ children }) => {
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(30); // Default 30 minutes
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const [sessionExpired, setSessionExpired] = useState(false);
  const [expiredMessage, setExpiredMessage] = useState('');

  // Session timeout functions
  const resetSessionTimeout = () => {
    lastActivityRef.current = Date.now();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (sessionTimeoutMinutes > 0) {
      timeoutRef.current = setTimeout(() => {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        const timeoutMs = sessionTimeoutMinutes * 60 * 1000;
        
        if (timeSinceLastActivity >= timeoutMs) {
          handleSessionExpired();
        } else {
          // Reset timeout with remaining time
          timeoutRef.current = setTimeout(() => {
            handleSessionExpired();
          }, timeoutMs - timeSinceLastActivity);
        }
      }, sessionTimeoutMinutes * 60 * 1000);
    }
  };

  const handleSessionExpired = () => {
    const message = `Your chat session has expired after ${sessionTimeoutMinutes} minutes of inactivity. This conversation is now closed. Please start a new chat to continue.`;
    setSessionExpired(true);
    setExpiredMessage(message);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const updateSessionTimeout = (minutes) => {
    setSessionTimeoutMinutes(minutes);
    localStorage.setItem('chatbotSessionTimeoutMinutes', minutes.toString());
    resetSessionTimeout();
  };

  // Activity tracking
  const trackActivity = () => {
    lastActivityRef.current = Date.now();
    resetSessionTimeout();
  };

  // Initialize session timeout
  const initializeSession = () => {
    lastActivityRef.current = Date.now();
    resetSessionTimeout();
  };

  // Load saved session timeout setting
  useEffect(() => {
    const savedTimeout = localStorage.getItem('chatbotSessionTimeoutMinutes');
    if (savedTimeout) {
      setSessionTimeoutMinutes(parseInt(savedTimeout));
    }
  }, []);

  // Activity tracking effect
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      trackActivity();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const value = {
    sessionTimeoutMinutes,
    updateSessionTimeout,
    trackActivity,
    initializeSession,
    sessionExpired,
    expiredMessage
  };

  return (
    <ChatbotAuthContext.Provider value={value}>
      {children}
    </ChatbotAuthContext.Provider>
  );
};







